import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

interface Identifiable {
  id: string;
}

interface PocketbookContentProps<TLeftItem extends Identifiable> {
  items: TLeftItem[];
  renderLeftItem: (item: TLeftItem, isSelected: boolean) => React.ReactNode;
  renderRightPanel: (selectedItem: TLeftItem | null) => React.ReactNode;
  loading?: boolean;
  searchPlaceholder?: string;
  onSearch?: (items: TLeftItem[], query: string) => TLeftItem[];
  defaultSelectedId?: string;
}

export const PocketbookContent = <TLeftItem extends Identifiable>({
  items,
  renderLeftItem,
  renderRightPanel,
  loading = false,
  searchPlaceholder = 'Search...',
  onSearch,
  defaultSelectedId,
}: PocketbookContentProps<TLeftItem>) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<TLeftItem | null>(null);
  const [hoveredItem, setHoveredItem] = useState<TLeftItem | null>(null);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim() || !onSearch) {
      return items;
    }
    return onSearch(items, searchQuery);
  }, [items, searchQuery, onSearch]);

  // Auto-select default item only once; otherwise preserve user selection
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (!appliedDefaultRef.current && defaultSelectedId) {
      const defaultItem = items.find((item) => item.id === defaultSelectedId);
      if (defaultItem) {
        setSelectedItem(defaultItem);
        appliedDefaultRef.current = true;
        return;
      }
    }

    if (!selectedItem && filteredItems.length > 0) {
      setSelectedItem(filteredItems[0]);
    }
  }, [filteredItems, selectedItem, defaultSelectedId, items]);

  const handleClickItem = (item: TLeftItem) => {
    setSelectedItem(item);
  };

  const handleHoverItem = (item: TLeftItem | null) => {
    setHoveredItem(item);
  };

  // Display hovered item if hovering, otherwise show selected item
  const displayedItem = hoveredItem || selectedItem;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-full -mx-4 -mt-4 -mb-4">
      {/* Search Header - extra top padding to clear the X button */}
      <div className="flex-shrink-0 px-4 pt-8 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-12 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="flex flex-1 min-h-0 overflow-hidden border-t">
        {/* Left: Item List */}
        <div className="w-[200px] border-r overflow-y-auto flex-shrink-0">
          {filteredItems.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">
              No results found
            </div>
          ) : (
            <div className="py-1">
              {filteredItems.map((item) => {
                const isSelected = selectedItem && selectedItem.id === item.id;
                const isHovered = hoveredItem && hoveredItem.id === item.id;
                const isPreviewing = isHovered && !isSelected;

                let itemClasses = 'w-full cursor-pointer transition-colors';
                if (isSelected) {
                  itemClasses +=
                    ' bg-blue-600 text-white border-l-4 border-blue-800';
                } else if (isPreviewing) {
                  itemClasses +=
                    ' bg-blue-50 text-blue-700 border-l-4 border-dashed border-blue-300';
                } else {
                  itemClasses +=
                    ' hover:bg-gray-50 border-l-4 border-transparent';
                }

                return (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => handleClickItem(item)}
                    onMouseEnter={() => handleHoverItem(item)}
                    onMouseLeave={() => handleHoverItem(null)}
                    className={itemClasses}
                  >
                    {renderLeftItem(item, !!isSelected)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Detail Panel */}
        <div className="flex-1 overflow-y-auto p-4 min-w-0">
          {displayedItem ? (
            renderRightPanel(displayedItem)
          ) : (
            <div className="text-sm text-gray-500 text-center py-8">
              Select an item to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
